"use client";

import Link from "next/link";
import { ArrowRight, Cpu, Activity, Brain, Network, PenTool, Radio, Bot, ChevronRight } from "lucide-react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from "recharts";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  enginesStats, enginesChildren, enginesFeatures, enginesUseCases,
  enginesWhy, enginesFaq, enginesCta, enginesRadar, enginesComposition,
} from "@/lib/data/engines-hub";

/* ============================================================
   BESPOKE 1 — Engine orchestrator signature diagram.
   A central X2 nucleus surrounded by 5 engine nodes connected
   via typed orchestration bus. Animates on load.
   ============================================================ */
function EngineOrchestratorDiagram() {
  const engines = [
    { code: "OSE", name: "Organic Simulation", color: "var(--color-cyan)", icon: Network },
    { code: "NGE", name: "Narrative Generation", color: "var(--color-purple)", icon: PenTool },
    { code: "CRE", name: "Cognitive Resonance", color: "var(--color-crimson)", icon: Brain },
    { code: "ECA", name: "Echo Chamber", color: "var(--color-amber)", icon: Radio },
    { code: "AOE", name: "Autonomous Ops", color: "var(--color-emerald)", icon: Bot },
  ];
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  const cx = 260;
  const cy = 260;
  const radius = 180;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="eCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.95" />
            <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="eGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="200" fill="url(#eGlow)" />
        {[110, 150, 195].map((r, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="rgb(255 255 255 / 0.07)" strokeWidth="0.6" strokeDasharray={i % 2 === 0 ? "1 3" : "2 6"} />
        ))}
        {/* Connecting lines from center to each engine */}
        {engines.map((e, i) => {
          const angle = (i / engines.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(cx + Math.cos(angle) * radius);
          const y = ROUND(cy + Math.sin(angle) * radius);
          return (
            <line key={`l-${e.code}`} x1={cx} y1={cy} x2={x} y2={y} stroke={e.color} strokeOpacity="0.3" strokeWidth="0.6" strokeDasharray="2 4" />
          );
        })}
        {/* Engine nodes */}
        {engines.map((e, i) => {
          const angle = (i / engines.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(cx + Math.cos(angle) * radius);
          const y = ROUND(cy + Math.sin(angle) * radius);
          return (
            <g key={e.code}>
              <circle cx={x} cy={y} r="36" fill={`color-mix(in oklch, ${e.color} 20%, transparent)`} stroke={e.color} strokeWidth="0.8" />
              <circle cx={x} cy={y} r="30" fill="rgb(10 10 18 / 0.7)" />
              <text x={x} y={y - 2} textAnchor="middle" fontSize="11" fontWeight="900" fill="white" letterSpacing="0.6">
                {e.code}
              </text>
              <text x={x} y={y + 9} textAnchor="middle" fontSize="5" fill="rgb(255 255 255 / 0.6)" letterSpacing="0.3">
                {e.name.split(" ")[0]?.toUpperCase()}
              </text>
            </g>
          );
        })}
        {/* Center hub */}
        <g transform={`translate(${cx}, ${cy})`}>
          <circle r="70" fill="url(#eCore)" />
          <circle r="52" fill="none" stroke="rgb(255 255 255 / 0.35)" strokeWidth="1" />
          <circle r="42" fill="rgb(10 10 18 / 0.92)" />
          <text x="0" y="-3" textAnchor="middle" fontSize="22" fontWeight="900" fill="white" letterSpacing="-0.5">
            X2
          </text>
          <text x="0" y="13" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#dc143c" letterSpacing="2">
            ENGINES
          </text>
          <text x="0" y="23" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            5 · 144D · 1M+
          </text>
        </g>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Cpu className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">5 Engines</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Brain className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">144-D Resonance</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Network className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">1M+ Personas</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Activity className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">98% Authentic</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Engine capability radar comparison.
   Five-engine overlay across five capability axes.
   ============================================================ */
function EngineCapabilityRadar() {
  const axes = ["Penetration", "Authenticity", "Speed", "Coverage", "Insight"] as const;
  const data = axes.map((axis) => {
    const row: Record<string, string | number> = { axis };
    enginesRadar.forEach((e) => {
      row[e.label] = e.values[axis.toLowerCase() as keyof typeof e.values];
    });
    return row;
  });
  return (
    <div className="relative w-full h-[420px]">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} margin={{ top: 30, right: 80, bottom: 30, left: 80 }}>
          <PolarGrid stroke="rgb(255 255 255 / 0.1)" />
          <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "rgb(255 255 255 / 0.7)" }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: "rgb(255 255 255 / 0.4)" }} axisLine={false} />
          {enginesRadar.map((e) => (
            <Radar
              key={e.label}
              name={e.label}
              dataKey={e.label}
              stroke={e.color}
              fill={e.color}
              fillOpacity={0.12}
              strokeWidth={1.6}
            />
          ))}
          <Legend
            iconType="circle"
            wrapperStyle={{ fontSize: 10, paddingTop: 10 }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Engine composition matrix table.
   Shows which engine is primary / secondary / orchestrator
   for each capability.
   ============================================================ */
function EngineCompositionMatrix() {
  const cellLabel: Record<string, { color: string; text: string }> = {
    Primary: { color: "var(--color-crimson)", text: "PRIMARY" },
    Secondary: { color: "var(--color-purple)", text: "SECONDARY" },
    "Informs": { color: "var(--color-cyan)", text: "INFORMS" },
    Signals: { color: "var(--color-cyan)", text: "SIGNALS" },
    Simulates: { color: "var(--color-amber)", text: "SIMULATES" },
    "Generates voices": { color: "var(--color-purple)", text: "GENERATES" },
    "Maps preferences": { color: "var(--color-crimson)", text: "MAPS" },
    "Routes by affinity": { color: "var(--color-amber)", text: "ROUTES" },
    "Provides baseline": { color: "var(--color-cyan)", text: "BASELINE" },
    "Optimizes output": { color: "var(--color-purple)", text: "OPTIMIZES" },
    "Measures resonance": { color: "var(--color-crimson)", text: "MEASURES" },
    "Verifies flow": { color: "var(--color-amber)", text: "VERIFIES" },
    "Re-simulates": { color: "var(--color-cyan)", text: "RE-SIM" },
    Regenerates: { color: "var(--color-purple)", text: "REGEN" },
    "Re-maps": { color: "var(--color-crimson)", text: "RE-MAP" },
    "Re-routes": { color: "var(--color-amber)", text: "RE-ROUTE" },
    Orchestrates: { color: "var(--color-emerald)", text: "ORCHESTRATES" },
    Executes: { color: "var(--color-emerald)", text: "EXECUTES" },
    Reports: { color: "var(--color-emerald)", text: "REPORTS" },
    "—": { color: "rgb(120 120 130)", text: "—" },
  };
  const cols = [
    { key: "ose", label: "OSE", accent: "var(--color-cyan)" },
    { key: "nge", label: "NGE", accent: "var(--color-purple)" },
    { key: "cre", label: "CRE", accent: "var(--color-crimson)" },
    { key: "eca", label: "ECA", accent: "var(--color-amber)" },
    { key: "aoe", label: "AOE", accent: "var(--color-emerald)" },
  ] as const;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[rgb(var(--border)/0.15)]">
            <th className="text-left py-4 px-3 font-bold text-xs uppercase tracking-wider">Capability</th>
            {cols.map((c) => (
              <th key={c.key} className="text-left py-4 px-3 font-bold text-xs uppercase tracking-wider" style={{ color: c.accent }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {enginesComposition.map((row, i) => (
            <tr
              key={row.capability}
              className="border-b border-[rgb(var(--border)/0.08)] hover:bg-[rgb(var(--surface)/0.4)] transition-colors"
            >
              <td className="py-3 px-3">
                <span className="font-semibold text-xs">{row.capability}</span>
              </td>
              {cols.map((c) => {
                const v = row[c.key];
                const meta = cellLabel[v] ?? cellLabel["—"]!;
                return (
                  <td key={c.key} className="py-3 px-3">
                    <span
                      className="inline-block text-[9px] font-bold tracking-wider px-2 py-1 rounded"
                      style={{
                        color: meta.color,
                        background: `color-mix(in oklch, ${meta.color} 12%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${meta.color} 25%, transparent)`,
                      }}
                    >
                      {meta.text}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function EnginesHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-20 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Perception X2 Engine Stack</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Five Proprietary{" "}
                    <span className="bg-gradient-to-br from-[var(--color-crimson)] via-[var(--color-purple)] to-[var(--color-cyan)] bg-clip-text text-transparent">
                      AI Engines
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    Five engines, each purpose-built for a specific cognitive layer of perception amplification. Together: autonomous orchestration at machine speed.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    Organic Simulation, Narrative Generation, Cognitive Resonance, Echo Chamber Architecture, and Autonomous Operations — composed at runtime through a typed orchestration bus to match the strategic posture of every engagement.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/organic-simulation-engine"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/30]"
                    >
                      Explore OSE Engine <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/platform/overview"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      View Platform Stack
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {enginesStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <EngineOrchestratorDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="The Five Engines"
          title="Each Engine. One Cognitive Layer."
          description="Perception X2 composes five engines at runtime — each addressing one cognitive layer of the amplification stack. Engage them standalone or as a composed unit."
          items={enginesChildren}
          accent="crimson"
        />
      }
      features={
        <>
          <Section id="radar" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Engine Capability Profile"
                title="Five Engines Across Five Dimensions"
                description="Each engine is profiled across penetration, authenticity, speed, coverage, and insight. The radar below shows the complementarity that drives the composed stack's overall ceiling."
                accent="crimson"
              />
              <div className="glass rounded-2xl p-6">
                <EngineCapabilityRadar />
              </div>
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Engine Capabilities"
                title="What Each Engine Brings to the Stack"
                description="Eight foundational capabilities shared across the Perception X2 engine portfolio — each engineered to operate at machine speed with 15+ years of refinement."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {enginesFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Engine Applications"
          title="Six Operational Patterns"
          description="Each engine supports distinct operational patterns — pre-crisis simulation, multi-modal content factories, precision targeting, organic amplification, autonomous triggers, and continuous refinement."
          cases={enginesUseCases}
          accent="emerald"
        />
      }
      why={
        <>
          <Section id="composition" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Composition Matrix"
                title="Which Engine Does What"
                description="Eight capability layers, mapped across the five engines. Each row shows the engine responsible for primary execution, support, and orchestration."
                accent="cyan"
              />
              <div className="glass rounded-2xl p-6">
                <EngineCompositionMatrix />
              </div>
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why These Engines"
                title="The Differentiators That Compound"
                items={enginesWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Engine FAQ"
          title="Frequently Asked Questions"
          items={enginesFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={enginesCta.eyebrow}
          title={enginesCta.title}
          description={enginesCta.description}
          primary={enginesCta.primary}
          secondary={enginesCta.secondary}
          trustItems={enginesCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
